import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  useContext,
} from "react";
import { Checkbox, Input, Spin } from "antd";
import { searchAuditTrailUins } from "api/auditTrailApi";
import { GlobalContext } from "contexts/GlobalContext";

const DEBOUNCE_MS = 500;

function normalizeStringList(list) {
  const raw = Array.isArray(list) ? list : [];
  const strings = raw
    .map((v) => {
      if (v == null) return "";
      if (typeof v === "string" || typeof v === "number") return String(v);
      if (typeof v === "object") return v.value ?? v.label ?? "";
      return "";
    })
    .map((s) => String(s).trim())
    .filter(Boolean);
  return [...new Set(strings)].sort((a, b) => a.localeCompare(b));
}

function extractUinsFromResponse(res) {
  const payload = res?.data?.data ?? res?.data ?? res;
  const list = Array.isArray(payload)
    ? payload
    : payload && typeof payload === "object"
      ? payload.data ?? payload.uins ?? payload.uin ?? []
      : [];
  if (!Array.isArray(list)) return [];
  // New API shape: [{ key, value }]
  return list.map((x) => {
    if (x == null) return "";
    if (typeof x === "string" || typeof x === "number") return String(x);
    if (typeof x === "object") return x.value ?? x.label ?? x.key ?? "";
    return "";
  });
}

const UinBackendSetFilter = forwardRef((props, ref) => {
  const { filterChangedCallback, api, column, colDef } = props;
  const {
    globalState: { dark_Theme },
  } = useContext(GlobalContext);

  const [options, setOptions] = useState([]);
  const [selected, setSelected] = useState([]);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const selectedRef = useRef([]);
  const appliedRef = useRef([]);
  const rootRef = useRef(null);
  const filterKeyRef = useRef(
    column?.getColId?.() || colDef?.field || colDef?.colId || "uin"
  );

  const seqRef = useRef(0);
  const debounceRef = useRef(null);
  const defaultOptionsRef = useRef([]);

  const isSameSelection = useCallback((a, b) => {
    const aArr = Array.isArray(a) ? a : [];
    const bArr = Array.isArray(b) ? b : [];
    if (aArr.length !== bArr.length) return false;
    const aSet = new Set(aArr);
    for (const v of bArr) {
      if (!aSet.has(v)) return false;
    }
    return true;
  }, []);

  const computeDefaultOptionsFromGrid = useCallback(() => {
    const all = [];
    if (api?.forEachNode) {
      api.forEachNode((node) => {
        const v = node?.data?.uin ?? node?.data?.data?.uin ?? null;
        if (v != null && String(v).trim() !== "") all.push(String(v).trim());
      });
    }
    return normalizeStringList(all);
  }, [api]);

  const fetchOptions = useCallback(async (search) => {
    const seq = (seqRef.current += 1);
    setLoading(true);
    try {
      const res = await searchAuditTrailUins({ uin: search });
      const values = normalizeStringList(extractUinsFromResponse(res));
      if (seq !== seqRef.current) return;
      setOptions(values);
    } catch {
      if (seq !== seqRef.current) return;
      setOptions([]);
    } finally {
      if (seq === seqRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    const defaults = computeDefaultOptionsFromGrid();
    defaultOptionsRef.current = defaults;
    setOptions(defaults);
    return () => {
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
    };
  }, [computeDefaultOptionsFromGrid]);

  const scheduleSearch = useCallback(
    (nextQuery) => {
      setQuery(nextQuery);
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
      const q = String(nextQuery ?? "").trim();
      if (!q) {
        // Match the previous behavior: show UINs from the currently loaded grid rows
        const defaults = computeDefaultOptionsFromGrid();
        defaultOptionsRef.current = defaults;
        setOptions(defaults);
        setLoading(false);
        return;
      }
      debounceRef.current = window.setTimeout(() => {
        fetchOptions(q);
      }, DEBOUNCE_MS);
    },
    [computeDefaultOptionsFromGrid, fetchOptions]
  );

  const toggle = useCallback((uin, checked) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (checked) next.add(uin);
      else next.delete(uin);
      const nextArr = [...next];
      selectedRef.current = nextArr;
      return nextArr;
    });
  }, []);

  const handleApply = useCallback(() => {
    const next = [...(selectedRef.current || [])];
    if (!isSameSelection(appliedRef.current, next)) {
      appliedRef.current = next;
      filterChangedCallback();
    }
    api?.hidePopupMenu?.();
  }, [filterChangedCallback, api, isSameSelection]);

  const handleClear = useCallback(() => {
    const hadApplied = Array.isArray(appliedRef.current) && appliedRef.current.length > 0;
    appliedRef.current = [];
    selectedRef.current = [];
    setSelected([]);
    if (hadApplied) {
      filterChangedCallback();
    }
    api?.hidePopupMenu?.();
  }, [filterChangedCallback, api]);

  const syncStagedSelectionFromAppliedModel = useCallback(() => {
    const key = filterKeyRef.current;
    const model = api?.getFilterModel?.()?.[key] ?? null;
    const values = model?.values;
    const next = Array.isArray(values) ? values : [];
    appliedRef.current = next;
    selectedRef.current = next;
    setSelected(next);
    setQuery("");
    const defaults = computeDefaultOptionsFromGrid();
    defaultOptionsRef.current = defaults;
    setOptions(defaults);
    setLoading(false);
  }, [api, computeDefaultOptionsFromGrid]);

  const textColor = dark_Theme
    ? "rgba(255,255,255,0.85)"
    : "rgba(0,0,0,0.85)";
  const subtleTextColor = dark_Theme
    ? "rgba(255,255,255,0.65)"
    : "rgba(0,0,0,0.65)";

  useImperativeHandle(ref, () => ({
    isFilterActive() {
      return Array.isArray(appliedRef.current) && appliedRef.current.length > 0;
    },
    doesFilterPass() {
      return true;
    },
    afterGuiAttached() {
      // Best-practice behavior for Apply-style filters:
      // when the popup is (re)opened, reset staged selection to the last applied model.
      syncStagedSelectionFromAppliedModel();
    },
    getModel() {
      // Return the last APPLIED model (not the staged checkbox state).
      if (!appliedRef.current?.length) return null;
      return { filterType: "set", values: appliedRef.current };
    },
    setModel(model) {
      const values = model?.values;
      const next = Array.isArray(values) ? values : [];
      appliedRef.current = next;
      selectedRef.current = next;
      setSelected(next);
    },
  }));

  useEffect(() => {
    const onPointerDown = (e) => {
      const root = rootRef.current;
      if (!root) return;
      if (root.contains(e.target)) return;
      // Clicking outside closes the popup; discard un-applied changes.
      syncStagedSelectionFromAppliedModel();
    };
    document.addEventListener("pointerdown", onPointerDown, true);
    return () => document.removeEventListener("pointerdown", onPointerDown, true);
  }, [syncStagedSelectionFromAppliedModel]);

  return (
    <div
      ref={rootRef}
      style={{ width: 220, overflowX: "hidden", color: textColor }}
    >
      <Input
        value={query}
        onChange={(e) => scheduleSearch(e.target.value)}
        placeholder="Search…"
        size="small"
        style={{ margin: "8px 8px 6px", color: textColor }}
      />

      <div
        style={{
          maxHeight: 220,
          overflow: "auto",
          overflowX: "hidden",
          padding: "0 8px",
          marginBottom: 8,
        }}
      >
        {loading ? (
          <div style={{ padding: 12, display: "flex", justifyContent: "center" }}>
            <Spin size="small" />
          </div>
        ) : options.length ? (
          options.map((uin) => (
            <div key={uin} style={{ padding: "2px 4px" }}>
              <Checkbox
                checked={selected.includes(uin)}
                onChange={(e) => toggle(uin, e.target.checked)}
                style={{ maxWidth: "100%", color: textColor }}
              >
                <span
                  style={{
                    display: "inline-block",
                    maxWidth: "100%",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    verticalAlign: "bottom",
                    whiteSpace: "nowrap",
                    color: textColor,
                  }}
                  title={uin}
                >
                  {uin}
                </span>
              </Checkbox>
            </div>
          ))
        ) : (
          <div style={{ padding: 8, color: subtleTextColor }}>No matches</div>
        )}
      </div>

      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          gap: 8,
          padding: "0 8px 8px",
        }}
      >
        <button
          type="button"
          className="ag-standard-button ag-filter-apply-panel-button"
          onClick={handleClear}
        >
          Clear
        </button>
        <button
          type="button"
          className="ag-standard-button ag-filter-apply-panel-button"
          onClick={handleApply}
        >
          Apply
        </button>
      </div>
    </div>
  );
});

export default UinBackendSetFilter;

